import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';

import { LoginPopupDialogComponent } from '../components/login-popup-dialog/login-popup-dialog.component';
import { MetadataService, MetadataSlugs } from './metadata.service';

@Injectable({
  providedIn: 'root',
})
export class LoginPopupService {
  private _isMinimized = new BehaviorSubject<boolean>(false);
  private _popupContent = new BehaviorSubject<string>('');
  private _popupTitle = new BehaviorSubject<string>('');
  private _isEnabled = new BehaviorSubject<boolean>(false);

  public isMinimized$: Observable<boolean> = this._isMinimized.asObservable();
  public popupContent$: Observable<string> = this._popupContent.asObservable();
  public popupTitle$: Observable<string> = this._popupTitle.asObservable();
  public isEnabled$: Observable<boolean> = this._isEnabled.asObservable();

  constructor(
    private _dialog: MatDialog,
    private _metadataService: MetadataService
  ) {
    this.loadSettings();
  }

  private loadSettings(): void {
    // Load popup enabled setting
    this._metadataService.get<string>(MetadataSlugs.LoginPopupEnabled).subscribe({
      next: metadata => {
        this._isEnabled.next(metadata.value === 'true');
      },
      error: () => {
        this._isEnabled.next(false);
      },
    });

    // Load popup content
    this._metadataService.get<string>(MetadataSlugs.LoginPopupContent).subscribe({
      next: metadata => {
        this._popupContent.next(
          metadata.value || '<p>Welcome! Please read this important message.</p>'
        );
      },
      error: () => {
        this._popupContent.next('<p>Welcome! Please read this important message.</p>');
      },
    });

    // Load popup title
    this._metadataService.get<string>(MetadataSlugs.LoginPopupTitle).subscribe({
      next: metadata => {
        this._popupTitle.next(metadata.value || 'Important Message');
      },
      error: () => {
        this._popupTitle.next('Important Message');
      },
    });
  }

  public showLoginPopup(): void {
    if (!this._isEnabled.value) {
      return;
    }

    const dialogRef = this._dialog.open(LoginPopupDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      disableClose: true,
      data: {
        content: this._popupContent.value,
        title: this._popupTitle.value,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'minimize') {
        this._isMinimized.next(true);
      } else if (result === 'dismiss') {
        this._isMinimized.next(false);
        // Disable the popup for this session when dismissed
        this._isEnabled.next(false);
      }
    });
  }

  public reopenPopup(): void {
    this._isMinimized.next(false);
    this.showLoginPopup();
  }

  public dismissPermanently(): void {
    this._isMinimized.next(false);
  }

  public get isMinimized(): boolean {
    return this._isMinimized.value;
  }

  public get popupContent(): string {
    return this._popupContent.value;
  }

  public get popupTitle(): string {
    return this._popupTitle.value;
  }

  public get isEnabled(): boolean {
    return this._isEnabled.value;
  }
}
